import { MouseEvent, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { WPPost } from '../../types/wordpress';
import { Hub } from '../../types/hub';
import { wordpressAPI } from '../../services/wordpressApi';
import Card from '../ui/Card';
import ResourceCard from './ResourceCard';
import LoadingSkeleton from '../ui/LoadingSkeleton';
import { decodeHtmlEntities } from '../../utils/htmlEntities';
import { programHubHref } from '../../utils/hubChapter';
import { buildResourcePath } from '../../utils/resourceLinks';
import { sameHubPostIds } from '../../utils/libraryPlaces';
import type { CatalogListResponse } from '../../types/resourceCatalog';
import {
  beginPlaceTransition,
  isPlainClick,
  peekPlaceRoom,
  PlaceRoom,
  rememberPlaceRoom,
  rememberResourcePost,
} from '../../utils/placeMotion';
import { capturePageFrom } from '../../utils/pageFrom';

interface RelatedResourcesProps {
  post: WPPost;
  limit?: number;
  hubs: Hub[];
  placesReady: boolean;
  currentHubSlug?: string | null;
}

function hubForPost(hubs: Hub[], postId: number, currentHubSlug: string | null): Hub | null {
  const matches = hubs.filter((hub) => hub.resources.some((resource) => resource.wpPostId === postId));
  if (currentHubSlug) {
    const current = matches.find((hub) => hub.slug === currentHubSlug);
    if (current) return current;
  }
  return [...matches].sort((a, b) => a.title.localeCompare(b.title))[0] ?? null;
}

function retargetRoom(room: PlaceRoom, programSlug: string, hub: Hub, title: string): PlaceRoom {
  const hubTo = programHubHref(programSlug, hub.slug);
  const crumbs = room.crumbs.map((crumb) => {
    if (crumb.to?.includes('#hub-') || crumb.to?.startsWith('/hubs/')) {
      return { ...crumb, label: hub.title, to: hubTo, step: 'shallower' as const };
    }
    return crumb;
  });
  if (crumbs.length > 0 && !crumbs[crumbs.length - 1].to) {
    crumbs[crumbs.length - 1] = { label: title };
  }
  return {
    ...room,
    backTo: hubTo,
    backLabel: `Back to ${hub.title}`,
    crumbs,
  };
}

async function catalogList(params: URLSearchParams): Promise<CatalogListResponse['data']> {
  const response = await fetch(`/api/tools/resources?${params.toString()}`);
  if (!response.ok) return [];
  const body = (await response.json()) as CatalogListResponse;
  return body.success ? body.data : [];
}

export default function RelatedResources({
  post,
  limit = 3,
  hubs,
  placesReady,
  currentHubSlug = null,
}: RelatedResourcesProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [relatedPosts, setRelatedPosts] = useState<WPPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!placesReady) return;
    let cancelled = false;

    const fetchRelated = async () => {
      try {
        setLoading(true);
        const chosen = sameHubPostIds(post.id, hubs, currentHubSlug, limit);

        if (chosen.length < limit) {
          const mine = new URLSearchParams({ wpIds: String(post.id), perPage: '1' });
          const [current] = await catalogList(mine);
          const topics = current?.topics ?? [];
          if (topics.length > 0) {
            const topicParams = new URLSearchParams({
              topic: topics.join(','),
              perPage: String(Math.min(50, limit + chosen.length + 1)),
              sort: 'updated',
            });
            const matches = await catalogList(topicParams);
            for (const item of matches) {
              if (item.wpId === post.id || chosen.includes(item.wpId)) continue;
              chosen.push(item.wpId);
              if (chosen.length >= limit) break;
            }
          }
        }

        const categoryPosts =
          chosen.length >= limit || post.categories.length === 0
            ? []
            : await wordpressAPI.fetchPosts({
                categories: post.categories.slice(0, 2),
                per_page: limit + chosen.length + 1,
                orderby: 'date',
                order: 'desc',
              });
        for (const item of categoryPosts) {
          if (item.id === post.id || chosen.includes(item.id)) continue;
          chosen.push(item.id);
          if (chosen.length >= limit) break;
        }

        const byId = new Map(categoryPosts.map((item) => [item.id, item]));
        const missing = chosen.filter((id) => !byId.has(id));
        if (missing.length > 0) {
          const fetched = await wordpressAPI.fetchPosts({
            include: missing.join(','),
            per_page: missing.length,
          });
          for (const item of fetched) byId.set(item.id, item);
        }

        if (!cancelled) {
          setRelatedPosts(
            chosen.flatMap((id) => {
              const item = byId.get(id);
              return item ? [item] : [];
            })
          );
        }
      } catch (error) {
        console.error('Failed to fetch related resources:', error);
        if (!cancelled) setRelatedPosts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchRelated();
    return () => {
      cancelled = true;
    };
  }, [post, limit, hubs, placesReady, currentHubSlug]);

  if (!placesReady || loading) {
    return (
      <div className="mt-12">
        <h3 className="text-2xl font-semibold text-gray-900 mb-6">Related Resources</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <LoadingSkeleton lines={4} />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (relatedPosts.length === 0) {
    return null;
  }

  return (
    <div className="mt-12">
      <h3 className="text-2xl font-semibold text-gray-900 mb-6">Related Resources</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {relatedPosts.map((relatedPost) => {
          const hub = hubForPost(hubs, relatedPost.id, currentHubSlug);
          const title = decodeHtmlEntities(relatedPost.title.rendered);
          const to = buildResourcePath(relatedPost.slug, hub?.slug);
          const openRelated = (event: MouseEvent<HTMLAnchorElement>) => {
            if (!isPlainClick(event)) return;
            event.preventDefault();
            rememberResourcePost(relatedPost);
            if (hub?.program) {
              const room = peekPlaceRoom();
              const previousState =
                location.state && typeof location.state === 'object'
                  ? { ...(location.state as Record<string, unknown>) }
                  : {};
              const restoredState = { ...previousState };
              delete previousState.documentBack;
              delete previousState.restoreProgramScan;
              previousState.replacedDocument = {
                to: `${location.pathname}${location.search}`,
                title: decodeHtmlEntities(post.title.rendered),
                state: restoredState,
                room,
              };
              if (room) rememberPlaceRoom(retargetRoom(room, hub.program.slug, hub, title));
              beginPlaceTransition('replace', () => {
                navigate(to, { state: previousState });
              });
              return;
            }
            rememberPlaceRoom(null);
            beginPlaceTransition('fade', () => {
              navigate(buildResourcePath(relatedPost.slug, hub?.slug), {
                state: { from: capturePageFrom(location) },
              });
            });
          };
          return (
            <ResourceCard
              key={relatedPost.id}
              post={relatedPost}
              fromHubSlug={hub?.slug}
              onOpen={openRelated}
            />
          );
        })}
      </div>
    </div>
  );
}
